import { extractOptionState } from './cronState';
import type {
  CronFormData,
  CustomOptionState,
  DayOptionState,
  HourOptionState,
  MonthOptionState,
  WeekOptionState,
  YearOptionState,
} from './cronTypes';
import { cronWildcard } from './cronValidation';

/**
 * Build cron expression from option state
 * Cron format: minute hour day month dayOfWeek
 */
function buildCronFromYearState(state: YearOptionState): string {
  const day = state.days.length > 0 ? state.days.join(',') : cronWildcard;
  const month = state.months.length > 0 ? state.months.join(',') : cronWildcard;
  return `${state.minute} ${state.hour} ${day} ${month} ${cronWildcard}`;
}

function buildCronFromMonthState(state: MonthOptionState): string {
  const day = state.days.length > 0 ? state.days.join(',') : cronWildcard;
  return `${state.minute} ${state.hour} ${day} ${cronWildcard} ${cronWildcard}`;
}

function buildCronFromWeekState(state: WeekOptionState): string {
  const dayOfWeek =
    state.weekDays.length > 0 ? state.weekDays.join(',') : cronWildcard;
  return `${state.minute} ${state.hour} ${cronWildcard} ${cronWildcard} ${dayOfWeek}`;
}

function buildCronFromDayState(state: DayOptionState): string {
  return `${state.minute} ${state.hour} ${cronWildcard} ${cronWildcard} ${cronWildcard}`;
}

function buildCronFromHourState(state: HourOptionState): string {
  // Special handling: when scheduling every hour, hour field must be a wildcard
  return `${state.minute} ${cronWildcard} ${cronWildcard} ${cronWildcard} ${cronWildcard}`;
}

function buildCronFromCustomState(state: CustomOptionState): string {
  return [
    state.minute,
    state.hour,
    state.days || cronWildcard,
    state.months || cronWildcard,
    state.weekDays || cronWildcard,
  ].join(' ');
}

/**
 * Convert form data to cron expression
 * Cron format: minute hour day month dayOfWeek
 *
 * This function extracts the option state from CronFormData and builds
 * the expression using the appropriate builder function for that option type.
 */
export function buildCronExpression(data: CronFormData): string {
  const optionState = extractOptionState(data, data.every);

  switch (data.every) {
    case 'year':
      return buildCronFromYearState(optionState as YearOptionState);
    case 'month':
      return buildCronFromMonthState(optionState as MonthOptionState);
    case 'week':
      return buildCronFromWeekState(optionState as WeekOptionState);
    case 'day':
      return buildCronFromDayState(optionState as DayOptionState);
    case 'hour':
      return buildCronFromHourState(optionState as HourOptionState);
    case 'custom':
      return buildCronFromCustomState(optionState as CustomOptionState);
  }
}
